import styled from "styled-components";
import { MediaQuery } from "../../hooks/useDeviceType";
import {
  BOLDER_WEIGHT,
  EERIE_BLACK,
  GAINSBORO,
  GHOST_WHITE,
  LIGHT_GAINSBORO,
  LIGHT_WHITE,
  NORMAL_HEIGHT,
  NORMAL_WEIGHT,
  PRIMARY_FONT,
  PRIMARY_SIZE,
  SECONDARY_FONT,
  SECONDARY_SIZE,
  SLIM_WEIGHT,
  SMALL_SECONDARY_SIZE,
  SMALL_TITLE_SIZE,
} from "../../utils/variables";

export const Container = styled.section`
  max-width: 110rem;
  margin: 0 auto;
  padding: 10rem 12rem;

  @media ${MediaQuery.isTablet} {
    padding: 8rem 6rem;
  }

  @media ${MediaQuery.isMobile} {
    max-width: unset;
    padding: 6rem 3rem;
  }
`;

export const HeadingContainer = styled.div`
  display: flex;
  align-items: center;
  font-family: ${SECONDARY_FONT};
  margin-bottom: 4rem;

  &::after {
    content: "";
    width: 100%;
    height: 1px;
    display: block;
    background-color: ${GAINSBORO};
    opacity: 0.4;
    margin-left: 2rem;
  }
`;

export const Heading = styled.h2`
  margin-left: 1rem;
  font-family: ${PRIMARY_FONT};
  font-size: ${SMALL_TITLE_SIZE};
  white-space: nowrap;
`;

export const Content = styled.div`
  display: flex;

  @media ${MediaQuery.isCompact} {
    flex-direction: column;
  }
`;

export const Tabs = styled.div`
  position: relative;
  z-index: 1;
  overflow: hidden;
  list-style-type: none;
  display: flex;
  flex-direction: column;

  @media ${MediaQuery.isCompact} {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: max-content;
    grid-template-rows: 1fr;
    width: 100%;
    max-width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    touch-action: auto;
    overscroll-behavior-inline: contain;
    scroll-snap-type: x proximity;
    scrollbar-width: none;
    padding-bottom: 0.8rem;
    margin-bottom: 2rem;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

export const Tab = styled.button`
  cursor: pointer;
  display: inline-block;
  width: 100%;
  border: 0;
  border-radius: 0;
  font: inherit;
  text-align: left;
  transform-origin: top;
  will-change: transform, opacity;
  color: ${(props) => (props.$active ? GHOST_WHITE : "unset")};
  font-size: ${SMALL_SECONDARY_SIZE};
  line-height: ${NORMAL_HEIGHT};
  padding: 1rem 2rem;
  white-space: nowrap;
  border-left: ${(props) =>
    props.$active ? `2px solid ${GHOST_WHITE}` : `2px solid ${LIGHT_GAINSBORO}`};
  background-color: ${(props) => (props.$active ? LIGHT_WHITE : "transparent")};

  &:hover {
    color: ${GHOST_WHITE};
    background-color: ${LIGHT_WHITE};
  }

  &:focus-visible {
    position: relative;
    z-index: 2;
    outline: 2px solid ${GHOST_WHITE};
    outline-offset: -2px;
  }

  @media ${MediaQuery.isCompact} {
    width: auto;
    scroll-snap-align: start;
    padding: 0.8rem 1.1rem;
    font-size: 1.4rem;
    border-left: unset;
    border-bottom: ${(props) =>
      props.$active
        ? `2px solid ${GHOST_WHITE}`
        : `2px solid ${LIGHT_GAINSBORO}`};
  }
`;

export const Description = styled.div`
  margin-left: 3rem;
  display: block;
  animation: fadein 0.35s ease-out;

  @media ${MediaQuery.isCompact} {
    margin-left: unset;
  }

  @keyframes fadein {
    from {
      opacity: 0;
      transform: translateY(0.6rem);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* Firefox < 16 */
  @-moz-keyframes fadein {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  /* Safari, Chrome and Opera > 12.1 */
  @-webkit-keyframes fadein {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  /* Internet Explorer */
  @-ms-keyframes fadein {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  /* Opera < 12.1 */
  @-o-keyframes fadein {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

export const Subheading = styled.h3`
  font-family: ${PRIMARY_FONT};
  font-size: ${PRIMARY_SIZE};
  font-weight: ${NORMAL_WEIGHT};
  color: ${GAINSBORO};
  line-height: 3rem;
  margin-bottom: 1rem;

  @media ${MediaQuery.isCompact} {
    font-size: ${SECONDARY_SIZE};
  }
`;

export const SpecialSubheading = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.8rem;
  color: ${GHOST_WHITE};
  font-family: ${PRIMARY_FONT};
  font-size: ${PRIMARY_SIZE};
  font-weight: ${BOLDER_WEIGHT};
  text-decoration: none;
  white-space: nowrap;
  svg {
    width: 1.6rem;
    height: 1.6rem;
    flex-shrink: 0;
  }

  &:focus-visible {
    outline: 2px solid ${GHOST_WHITE};
    outline-offset: 0.4rem;
  }
`;

export const SpecialText = styled.p`
  font-family: ${SECONDARY_FONT};
  font-size: ${SMALL_SECONDARY_SIZE};
  margin-bottom: 2rem;
`;

export const Text = styled.p`
  color: ${GAINSBORO};
  font-weight: ${SLIM_WEIGHT};
  font-size: ${SECONDARY_SIZE};
  line-height: ${NORMAL_HEIGHT};
  margin-bottom: 2rem;
  display: flex;

  &::before {
    content: "▹";
    display: block;
    margin-right: 1rem;
  }

  a {
    text-decoration: none;
    font-weight: ${NORMAL_WEIGHT};
    color: ${GHOST_WHITE};
    display: inline-block;
    position: relative;
    white-space: nowrap;

    &:hover::after {
      transform: scaleX(1);
      transform-origin: bottom left;
    }

    &::after {
      content: "";
      position: absolute;
      width: 100%;
      transform: scaleX(0);
      height: 2px;
      bottom: 0;
      left: 0;
      background-color: ${GHOST_WHITE};
      transform-origin: bottom right;
      transition: transform 0.25s ease-out;
    }
  }
`;

export const Tags = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
`;

export const Tag = styled.div`
  display: block;
  white-space: nowrap;
  color: ${GAINSBORO};
  background-color: transparent;
  font-family: ${SECONDARY_FONT};
  font-weight: ${NORMAL_WEIGHT};
  font-size: ${SMALL_SECONDARY_SIZE};
  padding: 0.5rem 0.9rem;
  border: 1px solid rgba(218, 221, 216, 0.2);
  border-radius: 999px;

`;

export const TagToggle = styled.button`
  position: relative;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  color: ${GHOST_WHITE};
  background-color: ${EERIE_BLACK};
  font-family: ${SECONDARY_FONT};
  font-weight: ${NORMAL_WEIGHT};
  font-size: ${SMALL_SECONDARY_SIZE};
  padding: 0.5rem 0.9rem;
  border: 1px solid rgba(218, 221, 216, 0.2);
  border-radius: 999px;
  cursor: pointer;

  &:hover {
    color: ${GAINSBORO};
    border-color: rgba(218, 221, 216, 0.38);
  }

  &:focus-visible {
    outline: 2px solid ${GHOST_WHITE};
    outline-offset: 0.3rem;
  }

`;
export const LocationText = styled.p`
  font-family: ${SECONDARY_FONT};
  font-size: ${SMALL_SECONDARY_SIZE};
  background-color: ${GAINSBORO};
  color: ${EERIE_BLACK};
  width: fit-content;
  padding: 0.5rem 0.9rem;
  border: 1px solid transparent;
  border-radius: 999px;
`;

export const ClientNavigation = styled.nav`
  width: 19rem;
  flex-shrink: 0;

  @media ${MediaQuery.isCompact} {
    width: 100%;
    margin-bottom: 2rem;
  }
`;

export const ShowClientsButton = styled.button`
  position: relative;
  z-index: 2;
  width: 100%;
  margin-top: 1rem;
  padding: 1rem 1.2rem;
  color: ${GAINSBORO};
  background: ${EERIE_BLACK};
  border: 1px solid ${LIGHT_GAINSBORO};
  border-radius: 0.4rem;
  font-family: ${SECONDARY_FONT};
  font-size: 1.2rem;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;

  &:hover,
  &:focus-visible {
    color: ${GHOST_WHITE};
    background-color: ${LIGHT_WHITE};
  }

  @media ${MediaQuery.isCompact} {
    width: auto;
    margin-top: 0;
  }
`;

export const EngagementMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
  margin-bottom: 2rem;
`;
